/**
 * Local-calendar week boundaries (Monday–Sunday, ISO-style).
 * Used by DateRangePicker and dashboard date presets — not UTC-shifted.
 */

export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

export function endOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

/** Monday 00:00 local for the week containing `date`. */
export function startOfWeekMondayLocal(date: Date): Date {
  const d = startOfLocalDay(date);
  const day = d.getDay();
  const daysFromMonday = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - daysFromMonday);
  return d;
}

/** Sunday 23:59:59.999 local for the week whose Monday is the Monday of `date`'s week. */
export function endOfWeekSundayLocal(date: Date): Date {
  const monday = startOfWeekMondayLocal(date);
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  return endOfLocalDay(sunday);
}

/** This week: Monday 00:00 → end of today (local). */
export function thisWeekMonTodayLocalRange(): { startDate: Date; endDate: Date } {
  const now = new Date();
  return {
    startDate: startOfWeekMondayLocal(now),
    endDate: endOfLocalDay(now),
  };
}

/** Last complete week: previous Monday 00:00 → previous Sunday 23:59:59.999 (local). */
export function lastWeekMonSunLocalRange(): { startDate: Date; endDate: Date } {
  const now = new Date();
  const thisMonday = startOfWeekMondayLocal(now);
  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(lastMonday.getDate() - 7);
  return {
    startDate: lastMonday,
    endDate: endOfWeekSundayLocal(lastMonday),
  };
}

export function localDaysEqual(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function localDateRangeMatchesThisWeek(dr: {
  startDate: Date | null;
  endDate: Date | null;
}): boolean {
  const expected = thisWeekMonTodayLocalRange();
  if (!dr.startDate || !dr.endDate) return false;
  return (
    localDaysEqual(dr.startDate, expected.startDate) &&
    localDaysEqual(dr.endDate, expected.endDate)
  );
}

export function localDateRangeMatchesLastWeek(dr: {
  startDate: Date | null;
  endDate: Date | null;
}): boolean {
  const expected = lastWeekMonSunLocalRange();
  if (!dr.startDate || !dr.endDate) return false;
  return (
    localDaysEqual(dr.startDate, expected.startDate) &&
    localDaysEqual(dr.endDate, expected.endDate)
  );
}

/** This month: 1st 00:00 local → end of today. */
export function thisMonthStartThroughTodayLocalRange(): {
  startDate: Date;
  endDate: Date;
} {
  const now = new Date();
  return {
    startDate: new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0),
    endDate: endOfLocalDay(now),
  };
}

/** Last calendar month: 1st 00:00 → last day 23:59:59.999 (local). */
export function lastMonthLocalRange(): { startDate: Date; endDate: Date } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
  return { startDate: start, endDate: end };
}

export function localDateRangeMatchesThisMonth(dr: {
  startDate: Date | null;
  endDate: Date | null;
}): boolean {
  const expected = thisMonthStartThroughTodayLocalRange();
  if (!dr.startDate || !dr.endDate) return false;
  return (
    localDaysEqual(dr.startDate, expected.startDate) &&
    localDaysEqual(dr.endDate, expected.endDate)
  );
}

export function localDateRangeMatchesLastMonth(dr: {
  startDate: Date | null;
  endDate: Date | null;
}): boolean {
  const expected = lastMonthLocalRange();
  if (!dr.startDate || !dr.endDate) return false;
  return (
    localDaysEqual(dr.startDate, expected.startDate) &&
    localDaysEqual(dr.endDate, expected.endDate)
  );
}
